import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import type { ComplianceActor } from '@/shared/hooks/api/lvdComplianceFiles'
import { useComplianceActorDisplayLabel } from '@/shared/hooks/useComplianceActorDisplayLabel'

function formatCompletedAt(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(iso))
}

type ProductComplianceFileSignOffView = {
  completedAt: string | null
  completedByActor: ComplianceActor | null
}

type ProductComplianceFileSignOffActionsProps = {
  productFile: ProductComplianceFileSignOffView
  canComplete: boolean
  isPending: boolean
  i18nKeyPrefix: string
  onComplete: () => Promise<unknown>
  onUncomplete: () => Promise<unknown>
}

export function ProductComplianceFileSignOffActions({
  productFile,
  canComplete,
  isPending,
  i18nKeyPrefix,
  onComplete,
  onUncomplete,
}: ProductComplianceFileSignOffActionsProps) {
  const { t, i18n } = useTranslation('common')
  const actionsKey = `${i18nKeyPrefix}.actions`
  const actorLabel = useComplianceActorDisplayLabel(productFile.completedByActor)

  const isComplete = productFile.completedAt != null

  const handleComplete = async () => {
    try {
      await onComplete()
    } catch {
      toast.error(t(`${actionsKey}.completeFailed`))
    }
  }

  const handleUncomplete = async () => {
    try {
      await onUncomplete()
    } catch {
      toast.error(t(`${actionsKey}.uncompleteFailed`))
    }
  }

  return (
    <div className='flex flex-wrap items-center justify-end gap-2'>
      {isComplete &&
      productFile.completedAt != null &&
      productFile.completedByActor != null ? (
        <span className='text-xs text-muted-foreground'>
          {t(`${i18nKeyPrefix}.completedAt`, {
            date: formatCompletedAt(productFile.completedAt, i18n.language),
            actor: actorLabel,
          })}
        </span>
      ) : null}
      {isComplete ? (
        <Button
          type='button'
          variant='outline'
          size='sm'
          disabled={isPending}
          onClick={() => {
            void handleUncomplete()
          }}
        >
          {t(`${actionsKey}.uncomplete`)}
        </Button>
      ) : (
        <>
          {!canComplete ? (
            <span className='text-xs text-muted-foreground'>
              {t(`${actionsKey}.confirmedStandardsRequired`)}
            </span>
          ) : null}
          <Button
            type='button'
            size='sm'
            disabled={isPending || !canComplete}
            onClick={() => {
              void handleComplete()
            }}
          >
            {t(`${actionsKey}.complete`)}
          </Button>
        </>
      )}
    </div>
  )
}
